// Taşeron portalı — guest-activation (davetli personel onboarding'i; api-contract
// §5.10). Davet linki token'ı URL'den okunur. Akış: (1) aktivasyonu başlat
// (`/auth/guest/start` → hedefe OTP) → (2) kod + yeni parola (`/auth/guest/activate`)
// → auto-login supplier terminal token → panel. Parola politikası FE'de doğrulanır
// (min 8 + harf + rakam; §5.10.3). Başlatma butonla tetiklenir (çift OTP gönderimi /
// cooldown 429 önlenir). GL6 §4.7: T.C. adımı koşulsuzdur; yanlış değer bağlantıyı korur
// (ortak 5 deneme sınırına kadar), kilitlenme terminal ekranda biter (R10).

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  type GuestStartResult,
  Input,
  Spinner,
  currentCulture,
  guestActivate,
  guestStart,
  hasErrorCode,
  isValidTcKimlikNo,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

export function GuestActivatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [started, setStarted] = useState<GuestStartResult | null>(null);

  const startMutation = useMutation({
    mutationFn: () => guestStart({ token }),
    onSuccess: (result) => setStarted(result),
  });

  const policyMessage = t('validation.passwordPolicy');
  const schema = useMemo(
    () =>
      z.object({
        code: z.string().min(1, t('validation.codeRequired')),
        password: z
          .string()
          .min(8, policyMessage)
          .regex(/[A-Za-z]/, policyMessage)
          .regex(/\d/, policyMessage),
        tcKimlikNo: z
          .string()
          .trim()
          .min(1, t('guest.validation.tcRequired'))
          .refine((v) => isValidTcKimlikNo(v), t('personnelForm.validation.tcInvalid')),
      }),
    [t, policyMessage],
  );
  type FormValues = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', password: '', tcKimlikNo: '' },
  });

  const inflightRef = useRef(false);
  const activateMutation = useMutation({
    mutationFn: (values: FormValues) =>
      guestActivate({
        token,
        code: values.code,
        password: values.password,
        tcKimlikNo: values.tcKimlikNo.trim(),
      }),
    onSuccess: () => navigate('/', { replace: true }),
    onError: (error) => {
      // Mismatch / format: alana bağlanır ve odaklanır; kod ve parola KORUNUR.
      if (hasErrorCode(error, 'TC_KIMLIK_MISMATCH') || hasErrorCode(error, 'TC_KIMLIK_INVALID')) {
        setError(
          'tcKimlikNo',
          {
            type: 'server',
            message: hasErrorCode(error, 'TC_KIMLIK_INVALID')
              ? t('personnelForm.validation.tcInvalid')
              : resolveApiErrorMessage(error, t),
          },
          { shouldFocus: true },
        );
      }
    },
    onSettled: () => {
      inflightRef.current = false;
    },
  });

  // Terminal durum (R10): 5 hatalı denemeden sonra bağlantı iptal edilir; activate ve "Kodu
  // tekrar gönder" GUEST_INVITE_INVALID/EXPIRED döner — canlı form çıkmaz sokaktır. `start` de bunu
  // döner (iptal edilen / yeniden gönderilmekle geçersizleşen / süresi dolan bağlantı): "Aktivasyonu
  // başlat" düğmesi yalnız yeniden başarısız olurdu → onu da uç durum ekranına götür.
  const isLinkDead = (error: unknown) =>
    hasErrorCode(error, 'GUEST_INVITE_INVALID') || hasErrorCode(error, 'GUEST_INVITE_EXPIRED');
  const dead =
    (activateMutation.isError && isLinkDead(activateMutation.error)) ||
    (startMutation.isError && isLinkDead(startMutation.error));

  // Odak, durum değişince <body>'ye düşmesin (I-2): faz 2'de kod alanı, uç durumda kart.
  const deadRef = useRef<HTMLDivElement>(null);
  const resendErrorRef = useRef<HTMLDivElement>(null);
  const hasStarted = started !== null;
  useEffect(() => {
    if (hasStarted && !dead) setFocus('code');
  }, [hasStarted, dead, setFocus]);
  useEffect(() => {
    if (dead) deadRef.current?.focus();
  }, [dead]);
  // Faz 2'de "Kodu tekrar gönder" hatası (I-1): her yeni hata nesnesinde uyarıya odaklan.
  const resendError = hasStarted && startMutation.isError && !dead ? startMutation.error : null;
  useEffect(() => {
    if (resendError) {
      resendErrorRef.current?.focus();
      resendErrorRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [resendError]);

  // PERSONNEL_TC_* burada firma ekranlarından farklı anlam taşır (katılan kişi düzeltemez,
  // bağlantı korunur, deneme harcanmaz) → kod aramasını atlayıp sayfa-yerel anahtar kullan.
  function activationBanner(error: unknown): string | null {
    if (hasErrorCode(error, 'TC_KIMLIK_MISMATCH') || hasErrorCode(error, 'TC_KIMLIK_INVALID')) {
      return null; // alan hatasında gösterildi
    }
    if (hasErrorCode(error, 'PERSONNEL_TC_REQUIRED')) return t('guest.tcRequired');
    if (hasErrorCode(error, 'PERSONNEL_TC_ALREADY_EXISTS')) return t('guest.tcAlreadyExists');
    return resolveApiErrorMessage(error, t);
  }

  // Davet token'ı yoksa akış geçersiz.
  if (!token) {
    return (
      <Card>
        <CardHeader>
          <CardTitle as="h1">{t('guest.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Alert tone="danger">{t('guest.invalidLink')}</Alert>
          <Button asChild variant="secondary" className="w-full">
            <Link to="/login">{t('forgot.backToLogin')}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (dead) {
    return (
      <div ref={deadRef} tabIndex={-1} className="outline-none">
        <Card>
          <CardHeader>
            <CardTitle as="h1">{t('guest.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert tone="danger">{t('guest.linkDead')}</Alert>
            <Button asChild variant="secondary" className="w-full">
              <Link to="/login">{t('forgot.backToLogin')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Faz 1 — aktivasyonu başlat (OTP gönder).
  if (!started) {
    return (
      <Card>
        <CardHeader>
          <CardTitle as="h1">{t('guest.title')}</CardTitle>
          <CardDescription className="carbon:mb-6">{t('guest.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {startMutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(startMutation.error, t)}</Alert>
          ) : null}
          <p className="text-sm text-muted">{t('guest.intro')}</p>
          <Button
            type="button"
            loading={startMutation.isPending}
            onClick={() => startMutation.mutate()}
            className="w-full"
          >
            {startMutation.isPending ? <Spinner label={t('guest.start')} /> : t('guest.start')}
          </Button>
          <div className="text-center text-sm">
            <Link to="/login" className="text-primary hover:underline">
              {t('forgot.backToLogin')}
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Faz 2 — kod + yeni parola → aktivasyon (auto-login). GL3 §5.10.2: her iletişimin kendi kodu
  // vardır (`maskedTargets` = canlı kodu olan iletişimler, ilk eleman = `maskedTarget`); 2+ hedefte
  // metin `guest.sentToEach`. Yalnız string elemanlar render edilir: bozuk bir eleman
  // `Intl.ListFormat.format`'ı patlatmasın (W1). `?.`/`?? []` rolling deploy'da eski API'yi karşılar.
  const validTargets = (started.maskedTargets ?? []).filter(
    (x): x is string => typeof x === 'string',
  );
  const sentTargets = validTargets.length ? validTargets : [started.maskedTarget];
  const sentToText = new Intl.ListFormat(currentCulture(), { type: 'conjunction' }).format(
    sentTargets,
  );

  const banner = activateMutation.isError ? activationBanner(activateMutation.error) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h1">{t('guest.title')}</CardTitle>
        <CardDescription className="carbon:mb-6">{t('guest.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit((values) => {
            if (inflightRef.current) return;
            inflightRef.current = true;
            activateMutation.mutate(values);
          })}
          noValidate
        >
          <Alert tone="info">
            {t(sentTargets.length >= 2 ? 'guest.sentToEach' : 'guest.sentTo', {
              target: sentToText,
            })}
          </Alert>

          {banner ? <Alert tone="danger">{banner}</Alert> : null}
          {resendError ? (
            <div ref={resendErrorRef} tabIndex={-1} className="outline-none">
              <Alert tone="danger">{resolveApiErrorMessage(resendError, t)}</Alert>
            </div>
          ) : null}

          <Field htmlFor="code" label={t('guest.code')} error={errors.code?.message} required>
            <Input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              invalid={Boolean(errors.code)}
              {...register('code')}
            />
          </Field>

          <Field
            htmlFor="tcKimlikNo"
            label={t('guest.tc')}
            hint={t('guest.tcHint')}
            error={errors.tcKimlikNo?.message}
            required
          >
            <Input
              id="tcKimlikNo"
              inputMode="numeric"
              maxLength={11}
              autoComplete="off"
              invalid={Boolean(errors.tcKimlikNo)}
              aria-describedby={errors.tcKimlikNo ? 'tcKimlikNo-error' : 'tcKimlikNo-hint'}
              {...register('tcKimlikNo')}
            />
          </Field>

          <Field
            htmlFor="password"
            label={t('guest.newPassword')}
            hint={t('guest.passwordHint')}
            error={errors.password?.message}
            required
          >
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              invalid={Boolean(errors.password)}
              {...register('password')}
            />
          </Field>

          <Button type="submit" loading={activateMutation.isPending} className="mt-1 w-full">
            {activateMutation.isPending ? (
              <Spinner label={t('guest.activate')} />
            ) : (
              t('guest.activate')
            )}
          </Button>

          <div className="text-center text-sm">
            <button
              type="button"
              className="text-primary hover:underline disabled:opacity-60"
              disabled={startMutation.isPending}
              onClick={() => startMutation.mutate()}
            >
              {t('guest.resend')}
            </button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
